import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Delete,
  Visibility,
  Edit,
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
  Email as EmailIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import {
  getPayrollById,
  getEmployeePayrollsByPayrollId,
  deleteEmployeePayroll,
  sendSalarySlipEmail,
  downloadSalarySlipPDF
} from '@/services/HrService/PayrollService';

interface EmployeePayroll {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  panNumber?: string;
  grossSalary: number;
  netSalary: number;
  emailSent?: number;
}

interface PayrollPeriod {
  id: string;
  month: string;
  year: number;
  payPeriodStart: string;
  payPeriodEnd: string;
  payDay: string;
  employees: EmployeePayroll[];
  totalGross: number;
  totalNet: number;
  status: 'draft' | 'processed' | 'completed';
}

const PayrollPeriodDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useAppTheme();

  const [period, setPeriod] = useState<PayrollPeriod | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);
  const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);
  const [confirmEmailDialog, setConfirmEmailDialog] = useState<{
    open: boolean;
    employeeId: string | null;
    employeeName: string;
    employeeEmail: string;
  }>({
    open: false,
    employeeId: null,
    employeeName: '',
    employeeEmail: '',
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    employeeId: string | null;
    employeeName: string;
  }>({
    open: false,
    employeeId: null,
    employeeName: '',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Search and Pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    const fetchPayrollDetails = async () => {
      if (!id) {
        navigate('/payroll');
        return;
      }

      setFetchLoading(true);
      try {
        // Fetch payroll basic info
        const { success, data, error } = await getPayrollById(parseInt(id));

        if (!success || !data) {
          throw new Error(error?.message || 'Failed to fetch payroll details');
        }

        // Fetch employee payrolls for this payroll period
        const employeePayrollsResponse = await getEmployeePayrollsByPayrollId(parseInt(id));

        let employeesList: EmployeePayroll[] = [];
        let totalGross = 0;
        let totalNet = 0;

        if (employeePayrollsResponse.success && employeePayrollsResponse.data) {
          employeesList = employeePayrollsResponse.data.map((ep: any) => {
            // Calculate net salary from components
            let netSalary = parseFloat(ep.gross_salary);

            (ep.salary_components || []).forEach((comp: any) => {
              const componentValue = comp.component_type === 'percentage'
                ? (parseFloat(ep.gross_salary) * parseFloat(comp.value)) / 100
                : parseFloat(comp.value);

              if (comp.operation === 'add') {
                netSalary += componentValue;
              } else {
                netSalary -= componentValue;
              }
            });

            totalGross += parseFloat(ep.gross_salary);
            totalNet += netSalary;

            return {
              id: ep.id.toString(),
              employeeId: ep.employee_id,
              employeeName: ep.employee_name,
              employeeEmail: ep.employee_email || '',
              bankName: ep.bank_name,
              accountNumber: ep.account_number,
              ifscCode: ep.ifsc_code,
              panNumber: ep.pan_number,
              grossSalary: parseFloat(ep.gross_salary),
              netSalary,
              emailSent: ep.email_sent || 0
            };
          });
        }

        // Helper function to format date as DD/MM/YYYY
        const formatDate = (dateString: string) => {
          const date = new Date(dateString);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        };

        // Map the API response to our PayrollPeriod interface
        const mappedPeriod: PayrollPeriod = {
          id: data.id.toString(),
          month: new Date(data.start_date).toLocaleString('default', { month: 'long' }),
          year: new Date(data.start_date).getFullYear(),
          payPeriodStart: formatDate(data.start_date),
          payPeriodEnd: formatDate(data.end_date),
          payDay: formatDate(data.pay_date),
          employees: employeesList,
          totalGross,
          totalNet,
          status: data.status === 'approved' ? 'processed' : data.status === 'sent' ? 'completed' : 'draft',
        };

        setPeriod(mappedPeriod);
      } catch (err: any) {
        console.error('Error fetching payroll details:', err);
        setSnackbar({
          open: true,
          message: err.message || 'Failed to load payroll details',
          severity: 'error',
        });
        // Redirect back to payroll list after a short delay
        setTimeout(() => navigate('/payroll'), 2000);
      } finally {
        setFetchLoading(false);
      }
    };

    fetchPayrollDetails();
  }, [id, navigate, location]);

  if (fetchLoading) {
    return (
      <Layout>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (!period) return null;

  // Filter employees based on search term
  const filteredEmployees = period.employees.filter((emp) => {
    const search = searchTerm.toLowerCase();
    return (
      emp.employeeName.toLowerCase().includes(search) ||
      emp.employeeEmail.toLowerCase().includes(search) ||
      emp.grossSalary.toString().includes(search) ||
      emp.netSalary.toString().includes(search)
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage);
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const paginatedEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);

  const hasPreviousPage = currentPage > 1;
  const hasNextPage = currentPage < totalPages;

  const handleOpenDeleteConfirm = (emp: EmployeePayroll) => {
    setDeleteConfirmDialog({
      open: true,
      employeeId: emp.id,
      employeeName: emp.employeeName,
    });
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmDialog({
      open: false,
      employeeId: null,
      employeeName: '',
    });
  };

  const handleConfirmDeleteEmployee = async () => {
    const empId = deleteConfirmDialog.employeeId;
    if (!empId) return;

    handleCloseDeleteConfirm();

    const emp = period.employees.find((e) => e.id === empId);
    if (!emp) return;

    setLoading(true);

    try {
      // Call the API to delete employee payroll
      const response = await deleteEmployeePayroll(parseInt(empId));

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to remove employee from payroll');
      }

      const updatedPeriod = {
        ...period,
        employees: period.employees.filter((e) => e.id !== empId),
        totalGross: period.totalGross - emp.grossSalary,
        totalNet: period.totalNet - emp.netSalary,
      };

      setPeriod(updatedPeriod);
      setSnackbar({ open: true, message: 'Employee removed successfully', severity: 'success' });
    } catch (error: any) {
      console.error('Error removing employee from payroll:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to remove employee from payroll',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEmailConfirmation = (emp: EmployeePayroll) => {
    setConfirmEmailDialog({
      open: true,
      employeeId: emp.id,
      employeeName: emp.employeeName,
      employeeEmail: emp.employeeEmail,
    });
  };

  const handleCloseEmailConfirmation = () => {
    setConfirmEmailDialog({
      open: false,
      employeeId: null,
      employeeName: '',
      employeeEmail: '',
    });
  };

  const handleConfirmSendEmail = async () => {
    if (!confirmEmailDialog.employeeId) return;

    const empId = confirmEmailDialog.employeeId;
    handleCloseEmailConfirmation();
    setSendingEmailId(empId);

    try {
      const response = await sendSalarySlipEmail(parseInt(empId));

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to send salary slip email');
      }

      // Update the employee's email_sent status in the local state
      if (period) {
        const updatedEmployees = period.employees.map(emp =>
          emp.id === empId ? { ...emp, emailSent: 1 } : emp
        );
        setPeriod({
          ...period,
          employees: updatedEmployees
        });
      }

      setSnackbar({
        open: true,
        message: 'Salary slip sent successfully to employee email!',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Error sending salary slip email:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send salary slip email',
        severity: 'error',
      });
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleDownloadSalarySlipPDF = async (empId: string) => {
    setDownloadingPdfId(empId);

    try {
      const response = await downloadSalarySlipPDF(parseInt(empId));

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to download salary slip');
      }

      setSnackbar({
        open: true,
        message: 'Salary slip downloaded successfully!',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Error downloading salary slip:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to download salary slip',
        severity: 'error',
      });
    } finally {
      setDownloadingPdfId(null);
    }
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/payroll')}
                sx={{
                  mb: 1,
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit'
                }}
              >
                Back to Payroll
              </Button>
              <Typography variant="h4" gutterBottom>
                Payroll - {period.month} {period.year}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Manage employees for this payroll
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/payroll/${id}/add-employee`)}
              sx={{ py: 1.5, px: 3 }}
            >
              Add Employee
            </Button>
          </Box>

          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="textSecondary">Pay Start Day</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {period.payPeriodStart} 
                </Typography>
              </CardContent>
            </Card>
             <Card>
              <CardContent>
                <Typography variant="caption" color="textSecondary">Pay End Day</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {period.payPeriodEnd}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="caption" color="textSecondary">Pay Day</Typography>
                <Typography variant="body2" fontWeight="medium">{period.payDay}</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Employees Table */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              {/* Search Bar */}
              <Box sx={{ p: 2, pb: 0 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by employee name, email or salary..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="right">Gross Salary</TableCell>
                      <TableCell align="right">Net Salary</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedEmployees.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">
                            {searchTerm
                              ? 'No employees match your search.'
                              : 'No employees added yet. Click "Add Employee" to start.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedEmployees.map((emp) => {
                        return (
                          <TableRow key={emp.id} hover>
                            <TableCell>
                              <Typography fontWeight="medium">{emp.employeeName}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {emp.employeeEmail || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">Rs.{emp.grossSalary.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Typography
                                fontWeight="bold"
                                sx={{
                                  color: isDarkMode ? '#90caf9' : 'primary.main'
                                }}
                              >
                                Rs.{emp.netSalary.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/payroll/${id}/employee/${emp.id}/view`)}
                                  >
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/payroll/${id}/employee/${emp.id}/edit`)}
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download Salary Slip">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadSalarySlipPDF(emp.id)}
                                    disabled={downloadingPdfId === emp.id}
                                    sx={{
                                      color: isDarkMode ? '#4ade80' : '#16a34a'
                                    }}
                                  >
                                    {downloadingPdfId === emp.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <DownloadIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleOpenDeleteConfirm(emp)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                                {period.status === 'processed' && (
                                  <Tooltip title={emp.emailSent === 1 ? "Email Already Sent" : "Send Salary Slip"}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenEmailConfirmation(emp)}
                                        disabled={sendingEmailId === emp.id || emp.emailSent === 1}
                                        sx={{
                                          color: emp.emailSent === 1
                                            ? (isDarkMode ? '#666' : '#999')
                                            : (isDarkMode ? '#90caf9' : '#1976d2'),
                                          cursor: emp.emailSent === 1 ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        {sendingEmailId === emp.id ? (
                                          <CircularProgress size={20} />
                                        ) : (
                                          <EmailIcon />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {filteredEmployees.length > 0 && totalPages > 1 && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  p: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ChevronLeft />}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!hasPreviousPage || loading}
                    >
                      Previous
                    </Button>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      minWidth: 100,
                      justifyContent: 'center'
                    }}>
                      <Typography variant="body2">
                        Page {currentPage} of {totalPages}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<ChevronRight />}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!hasNextPage || loading}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Email Confirmation Dialog */}
          <Dialog
            open={confirmEmailDialog.open}
            onClose={handleCloseEmailConfirmation}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color="primary" />
              <Typography variant="h6">Send Salary Slip</Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to send the salary slip to:
                </Typography>
                <Box sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: isDarkMode ? 'grey.900' : 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Employee Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    {confirmEmailDialog.employeeName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Email Address
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="primary.main">
                    {confirmEmailDialog.employeeEmail}
                  </Typography>
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                  The salary slip PDF will be sent as an email attachment.
                </Alert>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCloseEmailConfirmation}
                sx={{
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit'
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmSendEmail}
                startIcon={<EmailIcon />}
              >
                Send Email
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteConfirmDialog.open}
            onClose={handleCloseDeleteConfirm}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Box sx={{ py: 2 }}>
                <Typography variant="body1">
                  Are you sure you want to delete?
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCloseDeleteConfirm}
                sx={{
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit'
                }}
              >
                No
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDeleteEmployee}
              >
                Yes
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </Layout>
  );
};

export default PayrollPeriodDetails;


